import React, { useEffect, useState } from 'react';
import { api, cmsSubsectionOfTopic } from './api.js';
import { Card, Button, inputCls, Pill, Empty } from './ui.jsx';

/**
 * Who owns what, in CMS's own structure: a SECTION ("H. Customer Service")
 * holds SUBSECTIONS ("III. Medication Therapy Management (MTM) Programs").
 * Each subsection has one owning department with one or more accountable
 * people. Compliance can add further departments, each with its own
 * accountable people, to a whole section or to one subsection; every item
 * under it is then on all of them, and each department answers its own part.
 */
export default function RosterTab({ boot, people, onChanged, initialRoster = null }) {
  const [rows, setRows] = useState(initialRoster ? initialRoster.roster : null); // initialRoster: the render harness only
  const [review, setReview] = useState(initialRoster ? initialRoster.review || [] : []);
  const [extras, setExtras] = useState(initialRoster ? initialRoster.departments || [] : []);
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [adding, setAdding] = useState(null); // { section_letter } | { topic_key }
  const [addDept, setAddDept] = useState('');
  const [addAcc, setAddAcc] = useState([]);
  const topics = boot.edition.topics;
  const sections = boot.edition.sections;

  useEffect(() => {
    if (initialRoster) return;
    api('/roster').then((d) => { if (d?.success) { setRows(d.roster); setReview(d.review || []); setExtras(d.departments || []); } });
  }, [initialRoster]);

  const byKey = new Map((rows || []).map((r) => [r.topic_key, r]));
  const usersIn = (deptId) => (people?.users || []).filter((u) => deptId && u.department_id === deptId);
  const extrasOf = (key) => extras.filter((x) => x.topic_key === key);
  const propose = async () => {
    setBusy(true); setMsg(null);
    const d = await api('/roster/propose', { method: 'POST' });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not propose'); return; }
    setRows(d.roster); setReview(d.review || []); setDirty({}); onChanged?.();
    setMsg(`Proposed a department for ${d.roster.length} subsections. ${d.review.length ? `${d.review.length} to look at (amber rows).` : 'All matched with confidence.'}`);
  };
  const save = async () => {
    const changed = Object.entries(dirty).map(([topic_key, v]) => ({ topic_key, ...(byKey.get(topic_key) || {}), ...v, rationale: 'Set by compliance.' }));
    if (!changed.length) return;
    setBusy(true); setMsg(null);
    const d = await api('/roster', { method: 'PUT', body: { rows: changed.map((r) => ({ topic_key: r.topic_key, department_id: r.department_id || null, accountable_user_ids: Array.isArray(r.accountable_user_ids) ? r.accountable_user_ids : (r.owner_user_id ? [r.owner_user_id] : []), owner_user_id: (Array.isArray(r.accountable_user_ids) ? r.accountable_user_ids[0] : r.owner_user_id) || null, rationale: r.rationale })) } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not save'); return; }
    setRows(d.roster); setReview(d.review || []); setDirty({}); onChanged?.(); setMsg('Saved. Open Setup and click Re-apply to move the items in the open year.');
  };
  const confirm = async () => {
    setBusy(true); setMsg(null);
    const d = await api('/roster/confirm', { method: 'POST' });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not confirm'); return; }
    setRows(d.roster); onChanged?.(); setMsg('Roster confirmed.');
  };
  const addDepartment = async () => {
    if (!adding || !addDept) return;
    setBusy(true); setMsg(null);
    const d = await api('/roster/departments', { method: 'POST', body: { ...adding, department_id: addDept, accountable_user_ids: addAcc } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not add the department'); return; }
    setExtras(d.departments || []); setAdding(null); setAddDept(''); setAddAcc([]); onChanged?.();
    const where = adding.section_letter ? `section ${adding.section_letter}` : `subsection ${adding.topic_key}`;
    setMsg(`Added to ${where}: ${d.written} subsection${d.written === 1 ? '' : 's'}${d.skipped?.length ? ` (${d.skipped.length} already owned by that department)` : ''}. Open Setup and click Re-apply to put the items in the open year on it.`);
  };
  const removeDepartment = async (x) => {
    setBusy(true); setMsg(null);
    const d = await api('/roster/departments', { method: 'DELETE', body: { topic_key: x.topic_key, department_id: x.department_id } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not remove'); return; }
    setExtras(d.departments || []); onChanged?.();
  };
  const edit = (key, field, value) => setDirty((d) => {
    const next = { ...(d[key] || {}), [field]: value };
    // A new department brings its own people; the lead starts as the accountable person.
    if (field === 'department_id') { const u = usersIn(value); const lead = (u.find((x) => x.user_type === 'department_lead') || u[0] || {}).id; next.accountable_user_ids = lead ? [lead] : []; next.owner_user_id = lead || ''; }
    return { ...d, [key]: next };
  });
  const val = (key, field) => (dirty[key] && field in dirty[key] ? dirty[key][field] : (byKey.get(key)?.[field] ?? '')) || '';
  const accountableOf = (key) => {
    const v = dirty[key] && 'accountable_user_ids' in dirty[key] ? dirty[key].accountable_user_ids : byKey.get(key)?.accountable_user_ids;
    if (Array.isArray(v) && v.length) return v;
    const o = val(key, 'owner_user_id'); return o ? [o] : [];
  };
  const toggleAccountable = (key, userId) => {
    const cur = accountableOf(key);
    const next = cur.includes(userId) ? cur.filter((x) => x !== userId) : [...cur, userId];
    setDirty((d) => ({ ...d, [key]: { ...(d[key] || {}), accountable_user_ids: next, owner_user_id: next[0] || '' } }));
  };
  const unconfirmed = (rows || []).filter((r) => !r.confirmed_at).length;
  const chip = (on, label, onClick, title, key) => <button key={key || label} type="button" onClick={onClick} title={title} className={`text-[11px] px-2 py-0.5 rounded-full ring-1 ${on ? 'bg-orange-50 text-orange-800 ring-orange-200 font-semibold' : 'bg-white text-slate-500 ring-slate-200 hover:ring-orange-200'}`}>{on ? '✓ ' : ''}{label}</button>;

  const AddPicker = ({ where }) => (
    <div className="mt-2 flex flex-wrap items-end gap-2 bg-orange-50/40 border border-orange-200 rounded-xl px-3 py-2">
      <div className="text-xs font-semibold text-slate-700 basis-full">Add a department to {where}</div>
      <select className={`${inputCls} w-auto py-1`} value={addDept} onChange={(e) => { setAddDept(e.target.value); const u = usersIn(e.target.value); const lead = (u.find((x) => x.user_type === 'department_lead') || u[0] || {}).id; setAddAcc(lead ? [lead] : []); }}>
        <option value="">Choose a department</option>
        {(people?.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
      {addDept && (
        <div className="flex flex-wrap gap-1 items-center"><span className="text-[11px] text-slate-500">accountable:</span>{usersIn(addDept).map((u) => chip(addAcc.includes(u.id), u.name, () => setAddAcc(addAcc.includes(u.id) ? addAcc.filter((x) => x !== u.id) : [...addAcc, u.id]), undefined, u.id))}{!usersIn(addDept).length && <span className="text-xs text-rose-700">No one in this department</span>}</div>
      )}
      <Button disabled={busy || !addDept} onClick={addDepartment}>Add</Button>
      <Button kind="ghost" onClick={() => { setAdding(null); setAddDept(''); setAddAcc([]); }}>Cancel</Button>
    </div>
  );

  return (
    <Card title="Which department owns each CMS section and subsection" right={
      <div className="flex gap-2 flex-wrap">
        <Button kind="secondary" onClick={propose} disabled={busy}>{rows?.length ? 'Propose again from the organization' : 'Propose from the organization'}</Button>
        <Button kind="secondary" onClick={save} disabled={busy || !Object.keys(dirty).length}>Save changes</Button>
        <Button kind="good" onClick={confirm} disabled={busy || !rows?.length || unconfirmed === 0}>{unconfirmed ? `Confirm ${unconfirmed} ${unconfirmed === 1 ? 'row' : 'rows'}` : 'All confirmed'}</Button>
      </div>}>
      <p className="text-sm text-slate-600">CMS's checklist has {sections.length} sections and {topics.length} subsections, named here as CMS names them. Each subsection goes to an owning department; everyone in it is on its items, and the people you click under Accountable answer for it. Where more than one department is accountable, add a department to the whole section or to one subsection, with its own accountable people; each department then answers its own part.</p>
      {msg && <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mt-3">{msg}</div>}
      {rows === null ? <Empty>Loading…</Empty> : rows.length === 0 ? (
        <Empty>No roster yet. Click "Propose from the organization".</Empty>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full min-w-[1000px] text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="py-2 pr-3 font-semibold w-72">Subsection</th><th className="py-2 pr-3 font-semibold">Owning department</th><th className="py-2 pr-3 font-semibold">Accountable <span className="font-normal">(one or more)</span></th><th className="py-2 pr-3 font-semibold">Also on it</th><th className="py-2 pr-3 font-semibold">Why</th>
            </tr></thead>
            <tbody>
              {sections.map((s) => (
                <React.Fragment key={s.letter}>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <td colSpan={5} className="py-2 pr-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-bold text-slate-900">{s.letter}. {s.title}</span>
                        <button type="button" className="text-[11px] font-semibold text-orange-800 underline" onClick={() => { setAdding({ section_letter: s.letter }); setAddDept(''); setAddAcc([]); }}>+ Add a department to the whole section</button>
                      </div>
                      {adding?.section_letter === s.letter && <AddPicker where={`section ${s.letter}, every subsection`} />}
                    </td>
                  </tr>
                  {topics.filter((t) => t.section_letter === s.letter).map((t) => {
                    const r = byKey.get(t.key) || {};
                    const flagged = review.includes(t.key);
                    const deptId = val(t.key, 'department_id');
                    const more = extrasOf(t.key);
                    return (
                      <tr key={t.key} className={`border-b border-slate-50 align-top ${flagged ? 'bg-amber-50/60' : ''}`}>
                        <td className="py-2 pr-3"><div className="font-semibold text-slate-800">{cmsSubsectionOfTopic(t)}</div><div className="text-[11px] text-slate-500">CMS {t.key}{r.confirmed_at ? ' · confirmed' : ''}</div></td>
                        <td className="py-2 pr-3"><select className={`${inputCls} py-1`} value={deptId} onChange={(e) => edit(t.key, 'department_id', e.target.value)}><option value="">None</option>{(people?.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></td>
                        <td className="py-2 pr-3">
                          {(() => {
                            const chosen = accountableOf(t.key);
                            const pool = deptId ? usersIn(deptId) : (people?.users || []).filter((u) => chosen.includes(u.id));
                            const extra = (people?.users || []).filter((u) => chosen.includes(u.id) && !pool.some((p) => p.id === u.id));
                            const list = [...pool, ...extra];
                            if (!list.length) return <span className="text-xs text-rose-700">No one in this department</span>;
                            return <div className="flex flex-wrap gap-1 max-w-[260px]">{list.map((u) => chip(chosen.includes(u.id), u.name, () => toggleAccountable(t.key, u.id), chosen.includes(u.id) ? 'Click to remove' : 'Click to make accountable', u.id))}</div>;
                          })()}
                        </td>
                        <td className="py-2 pr-3 text-xs text-slate-700 max-w-[300px]">
                          {more.map((x) => (
                            <div key={x.id} className="flex items-start gap-1 mb-1">
                              <span><b>{x.department_name}</b>{(x.accountable_names || []).length ? ` · ${x.accountable_names.join(', ')} accountable` : ''}{x.applied_to === 'section' ? <span className="text-slate-400"> · whole section</span> : null}</span>
                              <button type="button" className="text-slate-400 hover:text-rose-700" title="Remove this department from the subsection" onClick={() => removeDepartment(x)}>×</button>
                            </div>
                          ))}
                          <button type="button" className="text-[11px] font-semibold text-orange-800 underline" onClick={() => { setAdding({ topic_key: t.key }); setAddDept(''); setAddAcc([]); }}>+ department</button>
                          {adding?.topic_key === t.key && <AddPicker where={cmsSubsectionOfTopic(t)} />}
                        </td>
                        <td className="py-2 pr-3 text-xs text-slate-600 max-w-[220px]">{r.rationale || ''}{typeof r.confidence === 'number' && r.source !== 'human' ? <Pill className="ml-1" tone={r.confidence >= 50 ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-amber-50 text-amber-800 ring-amber-200'}>{r.confidence}%</Pill> : null}{r.source === 'human' ? <Pill className="ml-1">by hand</Pill> : null}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
